/**
 * PaperBrainz chat widget — single source of truth for how the widget LOOKS
 * and BEHAVES.
 *
 * Both hosts render through this module:
 *   - the embed script     (src/widget/embed.ts)                 — real transport
 *   - the dashboard preview (src/components/chatbot/ChatPreview) — stubbed transport
 *
 * so what the customer configures is pixel-for-pixel what visitors see. Keep it
 * free of framework code and of anything that knows about our API: pure view
 * logic plus a DOM-attach helper, nothing that only works in one of the hosts.
 * Transport belongs in the host, behind WidgetHost.send.
 *
 * ── Provenance ───────────────────────────────────────────────────────────
 * Ported from the Wix app at /Users/ak/Wix/paper-brainz/src/shared/widget-ui.ts
 * so both products ship the same widget. It is a FORK, not a copy: this build
 * adds logo_shape / logo_fit / logo_padding, typing_indicator and hide_branding,
 * each marked "PaperBrainz addition" at its definition. When syncing a change
 * from the Wix side, re-apply those five and nothing else — any other
 * divergence is drift and should be reconciled rather than preserved.
 *
 * Everything renders inside a **shadow root**. That is what makes the two hosts
 * identical: the customer's site CSS can't leak in on the storefront, and the
 * Wix Design System's CSS can't leak in inside the dashboard. It also means this
 * file needs essentially no `!important` — the pre-shadow version carried ~200 of
 * them purely to win specificity fights against host pages. The single remaining
 * one pins the hero chip's logo padding; see `.mono.chip img`.
 *
 * There is deliberately **no remote default asset**. A hosted fallback logo
 * renders as a broken image the moment a host page's CSP blocks the origin —
 * which is exactly what happened inside the Wix dashboard iframe, even though
 * the URL itself returns 200. With no logo set we draw a monogram from the
 * assistant's name instead: no network, no broken image, and it reads as
 * intentional rather than unconfigured.
 */

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export interface WidgetSettings {
  theme?: 'light' | 'dark';
  primary_color?: string;

  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  widget_size?: 'small' | 'medium' | 'large';
  border_radius?: number;

  button_color?: string;
  button_icon_color?: string;
  button_icon?: string;

  bot_name?: string;
  logo_url?: string;
  use_logo_as_button?: boolean;
  show_logo_in_header?: boolean;

  /* ---- PaperBrainz additions (not in the Wix build) --------------------
   * These four are the only intentional divergence from the shared widget.
   * Anything else that differs is drift and should be reconciled.
   * ------------------------------------------------------------------- */

  /** Frame shape for the logo slots. Applies to the header avatar, the
   *  per-message bot avatar and the launcher — NOT the hero chip, whose
   *  12px rounded-square is part of the hero composition rather than a
   *  logo-framing choice. */
  logo_shape?: 'circle' | 'rounded' | 'square';
  /** `cover` crops to fill (right for photos and tight marks); `contain`
   *  letterboxes (right for wide wordmarks with built-in whitespace).
   *  Never `fill` — it distorts. */
  logo_fit?: 'cover' | 'contain';
  /** Inset inside the logo frame, px. Lets a mark breathe when the source
   *  image is cropped tight. Applies to the same three slots as logo_shape. */
  logo_padding?: 0 | 4 | 8 | 12;
  /** Typing animation while the assistant composes. */
  typing_indicator?: 'dots' | 'wave' | 'pulse';
  /** Removes the "Powered by PaperBrainz" strip. Pro-only — the public config
   *  endpoint force-clears this for lower tiers, so the widget can trust it. */
  hide_branding?: boolean;

  show_starters?: boolean;
  starters?: string[];

  welcome_title?: string;
  welcome_message?: string;
  placeholder_text?: string;

  font_family?: string;
  message_spacing?: 'compact' | 'normal' | 'spacious';
  auto_open?: boolean;

  /** 'solid' paints the header in primary_color flat; 'gradient' blends it into
   *  gradient_color. Solid is the default — an always-on gradient took the choice
   *  away from the merchant. */
  header_style?: 'solid' | 'gradient';
  /** Second stop for the gradient. Empty = a darkened primary_color. */
  gradient_color?: string;

  show_home_tab?: boolean;
  /** PaperBrainz addition. Which view the panel opens on. Ignored when
   *  show_home_tab is false, since chat is then the only view available. */
  default_view?: 'home' | 'chat';

  show_whatsapp?: boolean;
  whatsapp_number?: string;
  show_email?: boolean;
  email_address?: string;
  show_phone?: boolean;
  phone_number?: string;

  support_email?: string;
  support_phone?: string;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface SendResult {
  success: boolean;
  error?: string;
  /** 'conversation_limit' | 'visitor_daily_limit' — drives the disabled banner copy. */
  reason?: string;
  _status?: number;
}

export interface WidgetHost {
  /** Deliver a message. Call `onToken` as tokens stream in; resolve when done. */
  send(text: string, history: ChatTurn[], onToken: (token: string) => void): Promise<SendResult>;
  /** The visitor cleared the chat — the storefront host ends the billed conversation here. */
  onClear?(): void;
  /**
   * Preview mode. Renders inside the given element instead of taking over the
   * viewport: no visualViewport pinning, no focus stealing.
   */
  embedded?: boolean;
}

export interface WidgetHandle {
  /** Re-render with new settings (the dashboard preview calls this on every edit). */
  update(settings: WidgetSettings): void;
  destroy(): void;
}

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

/** Panel dimensions per size preset. Exported so the dashboard preview can
 *  reserve exactly the space the live widget will occupy. */
export const SIZES: Record<string, [number, number]> = {
  small: [360, 560],
  medium: [400, 640],
  large: [440, 720],
};

/** Faces in the font picker that ship with the OS. They must never be
 *  requested from Google Fonts — see ensureFont. */
const SYSTEM_FONTS = new Set([
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Courier New',
]);

const LAUNCHER = 60;
const LAUNCHER_GAP = 16;

/** Extra vertical room the launcher needs below the panel (desktop, open). */
export const LAUNCHER_STACK = LAUNCHER + LAUNCHER_GAP;

const ICON_PATHS: Record<string, string> = {
  'chat-circle':
    '<path d="M7.51 19.8C8.83 20.56 10.36 21 12 21c4.97 0 9-4.03 9-9s-4.03-9-9-9-9 4.03-9 9c0 1.64.44 3.17 1.2 4.49l.01.01c.07.13.11.19.13.25.02.06.02.11.01.17 0 .06-.02.13-.06.26l-.77 2.31-.01.01c-.16.49-.24.73-.18.89.05.14.16.25.3.3.16.06.4-.02.89-.18l.01-.01 2.31-.77c.13-.04.2-.06.26-.06.06-.01.11 0 .17.01.06.02.12.05.25.13l.01.01z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  note: '<path d="M13 20c-.1 0-.2 0-.32 0H7.2c-1.12 0-1.68 0-2.11-.22a2 2 0 01-.87-.87C4 18.48 4 17.92 4 16.8V7.2c0-1.12 0-1.68.22-2.11a2 2 0 01.87-.87C5.52 4 6.08 4 7.2 4h9.6c1.12 0 1.68 0 2.11.22a2 2 0 01.87.87C20 5.52 20 6.08 20 7.2v5.47c0 .12 0 .21 0 .33M13 20c.29 0 .47-.01.64-.05.2-.05.4-.13.58-.24.2-.12.37-.3.72-.64l4.12-4.13c.35-.34.52-.52.64-.72.11-.18.19-.38.24-.58.04-.17.05-.36.05-.64M13 20v-5.4c0-.56 0-.84.11-1.05a1 1 0 01.44-.44c.21-.11.49-.11 1.05-.11H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'chat-square':
    '<path d="M5.6 19.92l1.52-1.22.01-.01c.32-.25.48-.38.66-.47.16-.08.33-.14.51-.18.2-.04.41-.04.83-.04h8.68c1.12 0 1.68 0 2.11-.22a2 2 0 00.87-.87c.22-.43.22-.99.22-2.11V7.2c0-1.12 0-1.68-.22-2.11a2 2 0 00-.87-.87C19.48 4 18.92 4 17.8 4H6.2c-1.12 0-1.68 0-2.11.22a2 2 0 00-.87.87C3 5.52 3 6.08 3 7.2v11.47c0 1.07 0 1.6.22 1.87.19.24.48.38.78.38.35 0 .77-.33 1.6-.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  'paper-plane':
    '<path d="M10.31 13.69l4.85-4.85M20.11 5.89l-4.09 13.29c-.37 1.19-.55 1.79-.87 1.99a1 1 0 01-.91.08c-.34-.14-.62-.7-1.18-1.82l-2.59-5.18a1 1 0 00-.19-.34 1 1 0 00-.34-.19l-5.18-2.59c-1.12-.56-1.68-.84-1.82-1.18a1 1 0 01.08-.91c.2-.32.8-.5 1.99-.87l13.29-4.09c.94-.29 1.41-.43 1.72-.32a1 1 0 01.59.59c.12.31-.03.78-.32 1.72z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  bulb: '<path d="M9 21h6M12 3a6 6 0 00-6 6c0 1.21.36 2.34.98 3.29.95 1.45 1.43 2.18 1.49 2.29.55.96.45.62.52 1.73 0 .12 0 .31 0 .69a1 1 0 001 1h4a1 1 0 001-1c0-.38 0-.57.01-.69.07-1.11-.03-.77.52-1.73.06-.11.54-.84 1.49-2.29A6 6 0 0012 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  planet:
    '<path d="M6.9 15.16A7.48 7.48 0 0012 17c4.42 0 8-3.58 8-8 0-.57-.06-1.12-.17-1.66M6.9 15.16c-1.77-1.47-2.9-3.68-2.9-6.16 0-4.42 3.58-8 8-8 3.85 0 7.06 2.72 7.83 6.34M6.9 15.16c1.95-.45 4.28-1.38 6.63-2.73 2.73-1.58 4.95-3.42 6.3-5.09M6.9 15.16c-2.43.56-4.29.36-4.9-.72-.63-1.09.14-2.87 2-4.75M19.83 7.34c1.25-1.55 1.76-2.96 1.22-3.88-.54-.93-2.04-1.19-4.05-.86" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  leaf: '<path d="M6.83 17.08c7.07 4.24 12.73-1.41 12.02-12.02-10.61-.71-16.26 4.95-12.02 12.02zm0 0L5 18.91m1.83-1.83l3.83-3.83" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
};

const ICONS = {
  close:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>',
  back:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>',
  restart:
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 109-9 9 9 0 00-6.36 2.64L3 8"/><path d="M3 3v5h5"/></svg>',
  chevron:
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>',
  homeOutline:
    '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.6L12 3l9 6.6V20a1 1 0 01-1 1h-5v-6.2H9V21H4a1 1 0 01-1-1z"/></svg>',
  homeSolid:
    '<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor"><path d="M11.4 3.3a1 1 0 011.2 0l8.6 6.3a1 1 0 01.4.8V20a1.5 1.5 0 01-1.5 1.5h-4.3v-5.9a1 1 0 00-1-1h-3.6a1 1 0 00-1 1v5.9H5.9A1.5 1.5 0 014.4 20v-9.6a1 1 0 01.4-.8z"/></svg>',
  chatOutline:
    '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M20.5 11.6a7.9 7.9 0 01-8.5 7.9 8.9 8.9 0 01-3.4-.7L4 20.5l1.6-4.4a7.7 7.7 0 01-1.1-4.1 7.9 7.9 0 018.5-7.5 8 8 0 017.5 7.1z"/></svg>',
  chatSolid:
    '<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.6c-4.75 0-8.6 3.3-8.6 7.4 0 2.25 1.16 4.27 2.98 5.64v3.05c0 .5.57.78.96.48l2.55-1.94c.68.12 1.39.18 2.11.18 4.75 0 8.6-3.3 8.6-7.41S16.75 3.6 12 3.6z"/></svg>',
  send: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M2.4 20.6 21.6 12 2.4 3.4 2.4 10.1 15.5 12 2.4 13.9z"/></svg>',
  sendLarge:
    '<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M2.4 20.6 21.6 12 2.4 3.4 2.4 10.1 15.5 12 2.4 13.9z"/></svg>',
  whatsapp:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21l1.65-4.5A8.5 8.5 0 1112 20.5a8.4 8.4 0 01-4.2-1.12z"/><path d="M8.6 9.2c0 3.2 2.6 5.8 5.8 5.8.5 0 1-.4 1-.9v-.9l-1.9-.8-.9 1a4.9 4.9 0 01-2.4-2.4l1-.9-.8-1.9h-.9c-.5 0-.9.5-.9 1z"/></svg>',
  mail: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="M3.6 6.8l8.4 5.9 8.4-5.9"/></svg>',
  phone:
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.5 2.1L8.1 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.8.7a2 2 0 011.7 2z"/></svg>',
};

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

/** HTML-escape. Used for everything the visitor or merchant typed. */
export function esc(str: string | undefined | null): string {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Minimal markdown for bot replies. Escapes first, so it is XSS-safe. */
export function formatMd(str: string): string {
  return esc(str)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    )
    .replace(/\n/g, '<br>');
}

function parseColor(hex: string): { r: number; g: number; b: number } {
  let h = String(hex || '').trim().replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (!/^[0-9a-fA-F]{6}$/.test(h)) h = '2E5BFF';
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

/** Always returns hex, so the colour helpers can be composed with each other. */
function toHex(r: number, g: number, b: number): string {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function shade(hex: string, amount: number): string {
  const { r, g, b } = parseColor(hex);
  const f = (v: number) => (amount < 0 ? v * (1 + amount) : v + (255 - v) * amount);
  return toHex(f(r), f(g), f(b));
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = parseColor(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Rotate hue, for the extra stops of the hero mesh gradient. */
function hueShift(hex: string, deg: number): string {
  const { r, g, b } = parseColor(hex);
  const [rr, gg, bb] = [r / 255, g / 255, b / 255];
  const max = Math.max(rr, gg, bb);
  const min = Math.min(rr, gg, bb);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === rr) h = ((gg - bb) / d) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
  }
  h = (h * 60 + deg + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let p: number[] = [0, 0, 0];
  if (h < 60) p = [c, x, 0];
  else if (h < 120) p = [x, c, 0];
  else if (h < 180) p = [0, c, x];
  else if (h < 240) p = [0, x, c];
  else if (h < 300) p = [x, 0, c];
  else p = [c, 0, x];
  return toHex((p[0] + m) * 255, (p[1] + m) * 255, (p[2] + m) * 255);
}

/** WCAG relative luminance. */
function luminance(hex: string): number {
  const { r, g, b } = parseColor(hex);
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * Pick black or white for text sitting on `hex` — so a pale brand color doesn't
 * produce white-on-yellow.
 */
function readableOn(hex: string): string {
  return luminance(hex) > 0.45 ? '#101828' : '#ffffff';
}

function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Nudge `fg` until it is legible on `bg`. Used for the brand colour wherever it
 * acts as text/icon on the panel surface rather than as a fill. Without this a
 * near-black or near-white brand colour silently disappears in one of the themes.
 */
function ensureContrast(fg: string, bg: string, min = 3.2): string {
  let c = fg;
  const lighten = luminance(bg) < 0.5;
  for (let i = 0; i < 24 && contrastRatio(c, bg) < min; i++) {
    c = shade(c, lighten ? 0.09 : -0.09);
  }
  return c;
}

function initials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
  if (!parts.length) return '◆';
  return parts.map((p) => p[0] ?? '').join('').toUpperCase();
}

function digitsOnly(v: string | undefined): string {
  return String(v || '').replace(/[^0-9]/g, '');
}

function timeNow(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/* ------------------------------------------------------------------ *
 * Styles
 * ------------------------------------------------------------------ */

export function widgetCss(
  s: WidgetSettings,
  embedded: boolean,
  side: 'left' | 'right',
): string {
  const primary = s.primary_color || '#2E5BFF';
  const onPrimary = readableOn(primary);
  const dark = s.theme === 'dark';
  const radius = typeof s.border_radius === 'number' ? Math.min(s.border_radius, 28) : 18;
  const font = `'${s.font_family || 'Inter'}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;

  const surface = dark ? '#15171c' : '#ffffff';
  const surfaceAlt = dark ? '#1c1f26' : '#f5f6f8';
  const border = dark ? 'rgba(255,255,255,.09)' : 'rgba(16,24,40,.08)';
  const text = dark ? '#f5f6f8' : '#0d1117';
  const textSec = dark ? '#9aa4b2' : '#5b6472';
  const botBubble = dark ? '#23272f' : '#eceef2';

  // Solid by default. The gradient is opt-in and its second colour is the
  // merchant's to pick — deriving it silently is only the fallback.
  const gradientTo = s.gradient_color || shade(primary, -0.44);
  const isGradient = s.header_style === 'gradient';

  // Brand colour, made legible against the panel surface. Used wherever primary
  // is text/icon rather than a fill.
  const accent = ensureContrast(primary, surface);

  // Logo framing (PaperBrainz addition). A percentage radius keeps the shape
  // correct across the three differently-sized slots without per-slot maths.
  const logoRadius =
    s.logo_shape === 'square' ? '0' : s.logo_shape === 'rounded' ? '28%' : '50%';
  const logoFit = s.logo_fit === 'contain' ? 'contain' : 'cover';
  const logoPad = typeof s.logo_padding === 'number' ? s.logo_padding : 0;

  const heroBg = isGradient
    ? `radial-gradient(120% 90% at 12% 6%, ${rgba(hueShift(primary, 22), 0.4)} 0%, transparent 58%),
       radial-gradient(95% 80% at 94% 2%, ${rgba(hueShift(gradientTo, -18), 0.36)} 0%, transparent 54%),
       linear-gradient(150deg, ${primary} 0%, ${gradientTo} 100%)`
    : primary;

  const headerBg = isGradient ? `linear-gradient(135deg, ${primary}, ${gradientTo})` : primary;

  const spacing =
    s.message_spacing === 'compact'
      ? { gap: 8, padY: 14, padX: 14 }
      : s.message_spacing === 'spacious'
        ? { gap: 20, padY: 24, padX: 20 }
        : { gap: 13, padY: 18, padX: 16 };

  return `
:host { all: initial; }
* { box-sizing: border-box; margin: 0; padding: 0; }
button { font: inherit; color: inherit; }

.pb {
  --primary: ${primary};
  --on-primary: ${onPrimary};
  --accent: ${accent};
  --primary-soft: ${rgba(accent, 0.12)};
  --surface: ${surface};
  --surface-alt: ${surfaceAlt};
  --border: ${border};
  --text: ${text};
  --text-sec: ${textSec};
  --bot-bubble: ${botBubble};
  --radius: ${radius}px;
  --ease: cubic-bezier(.32,.72,0,1);
  --logo-radius: ${logoRadius};
  --logo-fit: ${logoFit};
  --logo-pad: ${logoPad}px;

  position: ${embedded ? 'relative' : 'fixed'};
  ${embedded ? 'width:100%; height:100%;' : ''}
  z-index: 2147483000;
  font-family: ${font};
  font-size: 15px;
  line-height: 1.5;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

/* ── Launcher ─────────────────────────────────────────────────────── */
.launcher {
  position: absolute;
  bottom: 0; ${side}: 0;
  width: ${LAUNCHER}px; height: ${LAUNCHER}px;
  border: none; border-radius: 50%;
  background: ${s.button_color || primary};
  color: ${s.button_icon_color || onPrimary};
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 20px ${rgba(s.button_color || primary, 0.4)}, 0 2px 6px rgba(16,24,40,.14);
  transition: transform .32s var(--ease), box-shadow .32s var(--ease);
  overflow: hidden;
  z-index: 2;
}
.launcher:hover { transform: scale(1.06); box-shadow: 0 10px 30px ${rgba(s.button_color || primary, 0.5)}; }
.launcher:active { transform: scale(.95); }
/* The launcher stays a circle whatever logo_shape says — that's the button's
   own shape, not a logo frame, and a squared-off launcher reads as broken
   against the panel. Fit and padding still apply so a tight mark can breathe. */
.launcher img { width: 100%; height: 100%; object-fit: var(--logo-fit); padding: var(--logo-pad); }

/* ── Panel ────────────────────────────────────────────────────────── */
.panel {
  position: absolute;
  ${side}: 0;
  bottom: ${embedded ? '0' : `${LAUNCHER_STACK}px`};
  width: 100%;
  ${embedded ? 'height:100%;' : `height: calc(100% - ${LAUNCHER_STACK}px);`}
  display: flex; flex-direction: column;
  background: var(--surface);
  border-radius: var(--radius);
  box-shadow: 0 12px 48px rgba(16,24,40,.18), 0 2px 8px rgba(16,24,40,.08);
  overflow: hidden;
  animation: rise .38s var(--ease);
  transform-origin: ${side === 'left' ? 'bottom left' : 'bottom right'};
}
@keyframes rise {
  from { opacity: 0; transform: translateY(18px) scale(.96); }
  to   { opacity: 1; transform: none; }
}

/* ── Chat header ──────────────────────────────────────────────────── */
.header {
  display: flex; align-items: center; gap: 11px;
  padding: 14px 14px 14px 12px;
  background: ${headerBg};
  color: ${onPrimary};
  flex-shrink: 0;
}
.icon-btn {
  width: 34px; height: 34px; flex-shrink: 0;
  border: none; border-radius: 10px;
  background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  opacity: .92;
  transition: background .2s var(--ease), opacity .2s var(--ease);
}
.icon-btn:hover { background: rgba(255,255,255,.16); opacity: 1; }
.header .id { display: flex; align-items: center; gap: 11px; flex: 1; min-width: 0; }
.name { font-size: 15.5px; font-weight: 650; letter-spacing: -.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.status { font-size: 12.5px; opacity: .82; display: flex; align-items: center; gap: 6px; margin-top: 1px; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; flex-shrink: 0; box-shadow: 0 0 0 2.5px rgba(74,222,128,.25); }

/* ── Monogram / avatar ────────────────────────────────────────────── */
.mono {
  border-radius: 50%; overflow: hidden; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-weight: 700; letter-spacing: -.02em;
  background: rgba(255,255,255,.2);
  color: ${onPrimary};
}
.mono img { width: 100%; height: 100%; object-fit: var(--logo-fit); }
.mono.lg { width: 40px; height: 40px; font-size: 15px; }
.mono.sm { width: 28px; height: 28px; font-size: 11.5px; background: linear-gradient(140deg, ${primary}, ${shade(primary, -0.32)}); color: ${onPrimary}; }
.mono.chip { width: 36px; height: 36px; border-radius: 12px; font-size: 14px; background: rgba(255,255,255,.22); }
/* When a logo image is set, the hero chip fills edge to edge — no inset on the
   container OR the image — and uses cover so the mark keeps its aspect ratio
   (fill would stretch it out of shape).
   Both rules match only when an <img> is present, i.e. only when a logo is set;
   with no logo the chip shows the initials monogram and is unaffected.
   The !important pins padding at 0 no matter what later gets added to .mono,
   .mono.chip or the shared .mono img rule. These are the only !important
   declarations in the file.
   :has() is unsupported on very old browsers, where the rule is simply ignored
   and padding stays 0 anyway via the * reset — so it degrades to the same result.
   Must sit after .mono.chip to win on source order. */
.mono.chip:has(img) { padding: 0 !important; }
.mono.chip img { object-fit: cover; padding: 0 !important; }

/* Merchant-controlled logo framing (PaperBrainz addition).
   Deliberately scoped to .lg and .sm — the two true logo slots. The hero chip
   is excluded: its 12px rounded-square and edge-to-edge cover are part of the
   hero composition, and the rules just above pin them. Placed AFTER .mono.chip
   so source order can't silently flip which rule wins (equal specificity is a
   known trap in this file).
   .mono img inherits --logo-fit; .mono.chip img overrides it back to cover at
   higher specificity, so the chip is unaffected by the merchant's choice. */
.mono.lg, .mono.sm { border-radius: var(--logo-radius); padding: var(--logo-pad); }

/* ── Views ────────────────────────────────────────────────────────── */
.views { flex: 1 1 auto; display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.view { flex: 1 1 auto; display: none; flex-direction: column; min-height: 0; overflow: hidden; }
.view.active { display: flex; animation: fade .3s var(--ease); }
@keyframes fade { from { opacity: 0; } to { opacity: 1; } }

/* ── Home ─────────────────────────────────────────────────────────── */
.home-scroll { flex: 1 1 auto; overflow-y: auto; min-height: 0; overscroll-behavior: none; background: var(--surface-alt); }
.home-scroll::-webkit-scrollbar { width: 0; }

/* Gradient mode layers two hue-shifted radial highlights over the blend, which
   reads as depth without shipping an image a host page's CSP could block. */
.hero {
  position: relative;
  padding: 20px 22px 62px;
  background: ${heroBg};
  color: ${onPrimary};
  overflow: hidden;
}
.hero-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; position: relative; z-index: 1; }
.hero h2 {
  margin: 22px 0 8px;
  font-size: 26px; font-weight: 750; line-height: 1.18; letter-spacing: -.025em;
  position: relative; z-index: 1;
}
.hero p { font-size: 14.5px; line-height: 1.5; opacity: .88; position: relative; z-index: 1; max-width: 34ch; }

/* Cards lift over the hero's bottom edge — the move that makes this read as a
   designed surface instead of stacked rectangles. */
.home-body { margin-top: -44px; padding: 0 14px 22px; position: relative; z-index: 2; display: flex; flex-direction: column; gap: 12px; }
.card {
  background: var(--surface);
  border-radius: 15px;
  box-shadow: 0 8px 28px rgba(16,24,40,.10), 0 1px 3px rgba(16,24,40,.06);
  overflow: hidden;
}
.row {
  display: flex; align-items: center; gap: 12px; width: 100%;
  padding: 15px 16px;
  border: none; background: none; cursor: pointer;
  text-align: left; text-decoration: none; color: var(--text);
  font-size: 14.5px; font-weight: 500;
  transition: background .2s var(--ease);
}
.row + .row { box-shadow: inset 0 1px 0 var(--border); }
.row:hover { background: var(--surface-alt); }
.row:hover .chev { transform: translateX(3px); color: var(--accent); }
.row-txt { flex: 1; min-width: 0; }
.row-title { display: block; }
.row-sub { display: block; font-size: 12.8px; font-weight: 400; color: var(--text-sec); margin-top: 2px; line-height: 1.4; }
.chev { color: var(--text-sec); flex-shrink: 0; display: flex; transition: transform .25s var(--ease), color .2s var(--ease); }
.row-icon {
  width: 38px; height: 38px; flex-shrink: 0; border-radius: 11px;
  background: var(--primary-soft); color: var(--accent);
  display: flex; align-items: center; justify-content: center;
}
.cta-send {
  flex-shrink: 0; color: var(--accent);
  display: flex; align-items: center; justify-content: center;
  transition: transform .25s var(--ease);
}
.row:hover .cta-send { transform: translateX(3px); }
.group-label {
  font-size: 11px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase;
  color: var(--text-sec); padding: 4px 4px 0;
}

/* ── Messages ─────────────────────────────────────────────────────── */
.messages {
  flex: 1 1 auto; min-height: 0;
  overflow-y: auto; overscroll-behavior: none; -webkit-overflow-scrolling: touch;
  padding: ${spacing.padY}px ${spacing.padX}px;
  display: flex; flex-direction: column; gap: ${spacing.gap}px;
  background: var(--surface-alt);
}
/* Bottom-anchor the thread. An auto top margin on the first child soaks up the
   spare room, so a short conversation sits just above the composer instead of
   stranding it under a wall of empty surface — most of the screen, on a phone
   in fullscreen. Once the content overflows, the auto margin resolves to 0 and
   scrolling still reaches the first message. */
.messages > *:first-child { margin-top: auto; }
.messages::-webkit-scrollbar { width: 5px; }
.messages::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

.msg { display: flex; align-items: flex-end; gap: 8px; animation: pop .28s var(--ease); }
.msg.user { flex-direction: row-reverse; }
@keyframes pop { from { opacity: 0; transform: translateY(8px) scale(.98); } to { opacity: 1; transform: none; } }

.bubble {
  max-width: 80%;
  padding: 10px 14px;
  font-size: 14.5px; line-height: 1.55;
  word-wrap: break-word; overflow-wrap: anywhere;
}
.msg.bot .bubble {
  background: var(--bot-bubble); color: var(--text);
  border-radius: 16px 16px 16px 4px;
}
.msg.user .bubble {
  background: var(--primary); color: var(--on-primary);
  border-radius: 16px 16px 4px 16px;
  box-shadow: 0 2px 10px ${rgba(primary, 0.28)};
}
.bubble code { background: rgba(127,127,127,.18); padding: 1px 5px; border-radius: 4px; font-size: .9em; }
.bubble a { color: inherit; text-decoration: underline; }
.stamp { font-size: 11px; color: var(--text-sec); margin-top: 4px; padding: 0 4px; }
.stamp.right { text-align: right; }

.typing { display: flex; gap: 4px; align-items: center; padding: 3px 2px; }
.typing i { width: 7px; height: 7px; border-radius: 50%; background: var(--text-sec); display: block; animation: blink 1.3s infinite ease-in-out; }
.typing i:nth-child(2) { animation-delay: .18s; }
.typing i:nth-child(3) { animation-delay: .36s; }
@keyframes blink { 0%,70%,100% { opacity: .25; transform: scale(.8); } 35% { opacity: .95; transform: scale(1); } }

/* Typing indicator variants (PaperBrainz addition). The default stays the
   three blinking dots; these two are opt-in via typing_indicator.
   Each variant overrides the shared .typing i rule, so the base animation has
   to be re-declared rather than layered on top. */

/* Wave — the three dots ride a sine, offset by a third of the cycle each. */
.typing.is-wave i { animation: wave 1.15s infinite ease-in-out; }
.typing.is-wave i:nth-child(2) { animation-delay: .13s; }
.typing.is-wave i:nth-child(3) { animation-delay: .26s; }
@keyframes wave {
  0%, 60%, 100% { transform: translateY(0); opacity: .35; }
  30%           { transform: translateY(-5px); opacity: 1; }
}

/* Pulse — one accent dot with a single ring radiating out of it. The other two
   dots are removed rather than hidden so the flex gap collapses with them. */
.typing.is-pulse i:nth-child(2), .typing.is-pulse i:nth-child(3) { display: none; }
.typing.is-pulse i {
  width: 9px; height: 9px; position: relative;
  background: var(--accent); animation: pulse-core 1.5s infinite ease-in-out;
}
.typing.is-pulse i::after {
  content: ''; box-sizing: border-box;
  position: absolute; inset: 0; border-radius: 50%;
  border: 2px solid var(--accent);
  animation: pulse-ring 1.5s infinite ease-out;
}
@keyframes pulse-core {
  0%, 100% { opacity: .55; } 50% { opacity: 1; }
}
@keyframes pulse-ring {
  0%   { transform: scale(1);   opacity: .65; }
  100% { transform: scale(2.6); opacity: 0; }
}
.streaming::after { content: '▋'; animation: caret .8s steps(1) infinite; opacity: .6; margin-left: 1px; }
@keyframes caret { 50% { opacity: 0; } }

.chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  padding: 9px 15px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 999px; color: var(--text);
  font-size: 13.5px; font-weight: 500; cursor: pointer;
  transition: border-color .2s var(--ease), color .2s var(--ease), transform .2s var(--ease), box-shadow .2s var(--ease);
}
.chip:hover { border-color: var(--accent); color: var(--accent); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16,24,40,.08); }

.intro { padding: 16px; background: var(--surface); border-radius: 15px; box-shadow: 0 2px 10px rgba(16,24,40,.05); }
.intro h3 { font-size: 15.5px; font-weight: 650; margin-bottom: 5px; letter-spacing: -.01em; }
.intro p { font-size: 14px; color: var(--text-sec); line-height: 1.5; }

/* ── Composer ─────────────────────────────────────────────────────── */
.composer { flex-shrink: 0; padding: 12px 14px; background: var(--surface); box-shadow: inset 0 1px 0 var(--border); }
.field {
  display: flex; align-items: flex-end; gap: 8px;
  background: var(--surface-alt);
  border: 1.5px solid transparent;
  border-radius: 24px;
  padding: 6px 6px 6px 16px;
  transition: border-color .2s var(--ease), background .2s var(--ease);
}
.field:focus-within { border-color: var(--accent); background: var(--surface); }
textarea {
  flex: 1; border: none; outline: none; resize: none;
  background: transparent; color: var(--text);
  font-family: inherit; font-size: 14.5px; line-height: 1.5;
  max-height: 96px; padding: 7px 0;
}
textarea::placeholder { color: var(--text-sec); }
.send {
  width: 36px; height: 36px; flex-shrink: 0;
  border: none; border-radius: 50%;
  background: var(--primary); color: var(--on-primary);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: transform .25s var(--ease), opacity .2s var(--ease);
}
.send:hover:not(:disabled) { transform: scale(1.08); }
.send:disabled { opacity: .35; cursor: default; }

.notice { padding: 16px; background: var(--surface-alt); text-align: center; flex-shrink: 0; box-shadow: inset 0 1px 0 var(--border); }
.notice p { font-size: 13.5px; color: var(--text-sec); line-height: 1.45; }
.notice p + p { margin-top: 6px; }
.notice a { color: var(--accent); font-weight: 600; }

/* ── Tab bar + attribution ────────────────────────────────────────── */
.tabbar { display: flex; flex-shrink: 0; background: var(--surface); box-shadow: inset 0 1px 0 var(--border); }
.tab {
  flex: 1; border: none; background: none; cursor: pointer;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 9px 0 7px;
  color: var(--text-sec); font-size: 11.5px; font-weight: 550;
  transition: color .2s var(--ease);
}
.tab:hover { color: var(--text); }
.tab.active { color: var(--accent); font-weight: 680; }

.powered {
  flex-shrink: 0; text-align: center;
  padding: 8px 0 10px;
  /* Holds the strip's full height when the credit is hidden and the element is
     empty, so a Pro widget has the same bottom spacing as everyone else's
     instead of the tab bar sitting flush against the panel edge.
     8 + 10 padding + a 12px line box for the 10px uppercase text. */
  min-height: 30px;
  background: var(--surface);
  font-size: 10px; font-weight: 600; letter-spacing: .1em; text-transform: uppercase;
  color: var(--text-sec); opacity: .6;
}
.powered a { color: inherit; text-decoration: none; }
.powered a:hover { opacity: 1; text-decoration: underline; }

/* ── Mobile (storefront only) ─────────────────────────────────────── */
${
  embedded
    ? ''
    : `
@media (max-width: 640px) {
  .pb.open .launcher { display: none; }
  .pb.open .panel {
    border-radius: 0;
    inset: 0;
    width: 100%; height: 100%;
    /* Insets measured off visualViewport by syncViewport(). They are PADDING,
       not size: the panel still covers the whole layout viewport, so a stale
       value can only misposition content inside the panel — it can never
       open a gap for the host page to show through. */
    padding-top: var(--vv-top, 0px);
    padding-bottom: var(--kb-bottom, 0px);
  }
  .pb.open .header { padding-top: max(14px, env(safe-area-inset-top)); }
  .pb.open .hero   { padding-top: max(20px, calc(env(safe-area-inset-top) + 12px)); }
  .pb.open .powered { padding-bottom: max(10px, env(safe-area-inset-bottom)); }
  .hero h2 { font-size: 28px; }
  textarea { font-size: 16px; } /* iOS zooms in on anything smaller */
}
`
}
`;
}

/* ------------------------------------------------------------------ *
 * Mount
 * ------------------------------------------------------------------ */

interface Msg {
  text: string;
  sender: 'user' | 'bot';
  time: string;
  typing?: boolean;
}

export function mountWidget(
  root: HTMLElement,
  initial: WidgetSettings,
  host: WidgetHost,
): WidgetHandle {
  const embedded = !!host.embedded;
  const shadow = root.shadowRoot ?? root.attachShadow({ mode: 'open' });

  let s: WidgetSettings = { ...initial };
  let isOpen = embedded ? true : !!s.auto_open;
  // Resolved rather than hardcoded so the panel lands on whichever view the
  // customer chose. Written out inline because hasHome() is declared further
  // down and would still be in its temporal dead zone here.
  let tab: 'home' | 'chat' =
    s.show_home_tab !== false && s.default_view !== 'chat' ? 'home' : 'chat';
  let messages: Msg[] = [];
  let showStarters = true;
  let disabled = false;
  let disabledMsg = '';
  let sending = false;
  let lastSend = 0;

  const styleEl = document.createElement('style');
  const wrap = document.createElement('div');
  shadow.append(styleEl, wrap);

  /* ---- font ---- */
  let fontHref = '';
  function ensureFont() {
    const fam = s.font_family || 'Inter';
    // The picker offers six system faces alongside the Google ones. Asking
    // fonts.googleapis.com for "Georgia" returns a 400 and loads nothing, so
    // skip the request entirely — the family name in the CSS font stack is
    // all a system face needs.
    if (SYSTEM_FONTS.has(fam)) {
      fontHref = '';
      return;
    }
    const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fam)}:wght@400;500;600;700&display=swap`;
    if (href === fontHref) return;
    fontHref = href;
    // Font faces must live in the document, not the shadow root, to apply.
    // Guard on the href so switching fonts in the dashboard preview doesn't
    // pile up a <link> per keystroke.
    if (document.head.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  /* ---- derived ---- */
  const hasHome = () => s.show_home_tab !== false;
  /** View the panel opens on. Chat is forced when there is no home tab. */
  const openingTab = (): 'home' | 'chat' =>
    hasHome() && s.default_view !== 'chat' ? 'home' : 'chat';
  const botName = () => s.bot_name || 'Support';
  const starters = () => (s.show_starters === false ? [] : (s.starters || []).filter(Boolean));

  function channels() {
    const out: { label: string; icon: string; href: string; external: boolean }[] = [];
    if (s.show_whatsapp && digitsOnly(s.whatsapp_number)) {
      out.push({
        label: 'WhatsApp',
        icon: ICONS.whatsapp,
        href: `https://wa.me/${digitsOnly(s.whatsapp_number)}`,
        external: true,
      });
    }
    if (s.show_email && s.email_address) {
      out.push({ label: 'Email us', icon: ICONS.mail, href: `mailto:${s.email_address}`, external: false });
    }
    if (s.show_phone && s.phone_number) {
      out.push({
        label: 'Call us',
        icon: ICONS.phone,
        href: `tel:${String(s.phone_number).replace(/[^0-9+]/g, '')}`,
        external: false,
      });
    }
    return out;
  }

  /** Variant class for the typing indicator; empty string keeps the default dots. */
  function typingClass(): string {
    return s.typing_indicator === 'wave'
      ? ' is-wave'
      : s.typing_indicator === 'pulse'
        ? ' is-pulse'
        : '';
  }

  /** Logo when set, monogram otherwise. Never a remote fallback — see file header. */
  function mono(cls: string): string {
    return s.logo_url
      ? `<div class="mono ${cls}"><img src="${esc(s.logo_url)}" alt=""></div>`
      : `<div class="mono ${cls}">${esc(initials(botName()))}</div>`;
  }

  /* ---- html builders ---- */
  function messagesHtml(): string {
    let h = '';
    const list = starters();

    if (messages.length === 0) {
      h += `<div class="intro"><h3>${esc(s.welcome_title || 'Hi there! 👋')}</h3><p>${esc(
        s.welcome_message || 'Ask us anything — we usually reply in a few seconds.',
      )}</p></div>`;
    }

    for (const m of messages) {
      const body = m.typing
        ? `<div class="typing${typingClass()}"><i></i><i></i><i></i></div>`
        : m.sender === 'bot'
          ? formatMd(m.text)
          : esc(m.text);
      h +=
        `<div>` +
        `<div class="msg ${m.sender}">` +
        (m.sender === 'bot' ? mono('sm') : '') +
        `<div class="bubble">${body}</div>` +
        `</div>` +
        (m.typing ? '' : `<div class="stamp${m.sender === 'user' ? ' right' : ''}">${esc(m.time)}</div>`) +
        `</div>`;
    }

    // Both this and the intro card above used to be suppressed when Home was on,
    // on the theory that Home had already shown them. What it actually produced
    // was a chat tab with literally nothing in it — a full screen of empty
    // surface on mobile, and no way to start talking except typing. The two
    // screens are never visible at the same time, so showing them here too is
    // not a duplicate; it is the difference between an empty room and a greeting.
    if (showStarters && messages.length === 0 && list.length) {
      h += `<div class="chips">${list
        .map((t, i) => `<button class="chip" data-starter="${i}">${esc(t)}</button>`)
        .join('')}</div>`;
    }
    return h;
  }

  function homeHtml(): string {
    const list = starters();
    const chans = channels();

    const starterCard = list.length
      ? `<div class="card">${list
          .map(
            (t, i) =>
              `<button class="row" data-starter="${i}">
                 <span class="row-txt"><span class="row-title">${esc(t)}</span></span>
                 <span class="chev">${ICONS.chevron}</span>
               </button>`,
          )
          .join('')}</div>`
      : '';

    const chatCard = `<div class="card">
        <button class="row" data-act="to-chat">
          <span class="row-txt">
            <span class="row-title">Chat with ${esc(botName())}</span>
            <span class="row-sub">Have a question? We're here to help.</span>
          </span>
          <span class="cta-send">${ICONS.sendLarge}</span>
        </button>
      </div>`;

    const channelCard = chans.length
      ? `<div class="group-label">Other ways to reach us</div>
         <div class="card">${chans
           .map(
             (c) => `<a class="row" href="${esc(c.href)}"${
               c.external ? ' target="_blank" rel="noopener noreferrer"' : ''
             }>
               <span class="row-icon">${c.icon}</span>
               <span class="row-txt"><span class="row-title">${esc(c.label)}</span></span>
               <span class="chev">${ICONS.chevron}</span>
             </a>`,
           )
           .join('')}</div>`
      : '';

    return `
      <div class="view home${tab === 'home' ? ' active' : ''}" data-view="home">
        <div class="home-scroll">
          <div class="hero">
            <div class="hero-bar">
              ${mono('chip')}
              <button class="icon-btn" data-act="close" aria-label="Close">${ICONS.close}</button>
            </div>
            <h2>${esc(s.welcome_title || 'Hi there! 👋')}</h2>
            <p>${esc(s.welcome_message || 'Ask us anything — we usually reply in a few seconds.')}</p>
          </div>
          <div class="home-body">
            ${starterCard}
            ${chatCard}
            ${channelCard}
          </div>
        </div>
      </div>`;
  }

  function chatHtml(): string {
    const composer = disabled
      ? `<div class="notice">
           <p>${esc(disabledMsg || 'This assistant is unavailable right now.')}</p>
           ${
             s.support_email
               ? `<p>Contact us at <a href="mailto:${esc(s.support_email)}">${esc(s.support_email)}</a>${
                   s.support_phone ? ` or call ${esc(s.support_phone)}` : ''
                 }</p>`
               : ''
           }
         </div>`
      : `<div class="composer">
           <div class="field">
             <textarea rows="1" maxlength="2000" placeholder="${esc(
               s.placeholder_text || 'Type your message…',
             )}"></textarea>
             <button class="send" aria-label="Send">${ICONS.send}</button>
           </div>
         </div>`;

    return `
      <div class="view chat${tab === 'chat' ? ' active' : ''}" data-view="chat">
        <div class="messages">${messagesHtml()}</div>
        ${composer}
      </div>`;
  }

  function headerHtml(): string {
    return `
      <div class="header">
        ${hasHome() ? `<button class="icon-btn" data-act="to-home" aria-label="Back">${ICONS.back}</button>` : ''}
        <div class="id">
          ${s.show_logo_in_header === false ? '' : mono('lg')}
          <div style="min-width:0">
            <div class="name">${esc(botName())}</div>
            <div class="status"><span class="dot"></span>Typically replies instantly</div>
          </div>
        </div>
        <button class="icon-btn" data-act="clear" aria-label="Restart conversation" title="Restart conversation">${ICONS.restart}</button>
        <button class="icon-btn" data-act="close" aria-label="Close">${ICONS.close}</button>
      </div>`;
  }

  function tabHtml(which: 'home' | 'chat'): string {
    const on = tab === which;
    const icon =
      which === 'home'
        ? on
          ? ICONS.homeSolid
          : ICONS.homeOutline
        : on
          ? ICONS.chatSolid
          : ICONS.chatOutline;
    const label = which === 'home' ? 'Home' : 'Chat';
    return `<button class="tab${on ? ' active' : ''}" data-tab="${which}">${icon}<span>${label}</span></button>`;
  }

  function tabbarHtml(): string {
    if (!hasHome()) return '';
    return `<div class="tabbar">${tabHtml('home')}${tabHtml('chat')}</div>`;
  }

  // Pro tiers can drop the credit. The gate lives in the config endpoint, not
  // here — a widget served with hide_branding already had the plan checked.
  //
  // The strip still RENDERS when hidden, just empty. Removing the element
  // entirely pulled the tab bar flush against the panel's bottom edge and the
  // whole thing read as cropped; keeping its padding preserves the breathing
  // room the layout was designed around.
  const poweredHtml = () =>
    s.hide_branding
      ? `<div class="powered" aria-hidden="true"></div>`
      : `<div class="powered">Powered by <a href="https://paperbrainz.com" target="_blank" rel="noopener">PaperBrainz</a></div>`;

  /* ---- render ---- */
  function render() {
    ensureFont();
    const pos = s.position || 'bottom-right';
    const side: 'left' | 'right' = pos.includes('left') ? 'left' : 'right';
    styleEl.textContent = widgetCss(s, embedded, side);

    const [w, h] = SIZES[s.widget_size || 'medium'] || SIZES.medium;

    const launcher = `<button class="launcher" aria-label="${isOpen ? 'Close chat' : 'Open chat'}">${
      isOpen
        ? ICONS.close
        : s.use_logo_as_button && s.logo_url
          ? `<img src="${esc(s.logo_url)}" alt="">`
          : `<svg width="26" height="26" viewBox="0 0 24 24" fill="none">${
              ICON_PATHS[s.button_icon || 'chat-circle'] || ICON_PATHS['chat-circle']
            }</svg>`
    }</button>`;

    wrap.className = `pb${isOpen ? ' open' : ''}`;
    wrap.innerHTML = isOpen
      ? `<div class="panel">${headerHtml()}<div class="views">${
          hasHome() ? homeHtml() : ''
        }${chatHtml()}</div>${tabbarHtml()}${poweredHtml()}</div>${embedded ? '' : launcher}`
      : launcher;

    applyFrame(w, h, side, pos);
    bind();
    syncHeader();
    scrollDown();
    if (!embedded) {
      // Lock the page only while the panel actually owns the screen — never for
      // the launcher alone, and never on desktop.
      lastMobileFull = mobileFull();
      if (lastMobileFull) lockScroll();
      else unlockScroll();
      schedule();
    }
  }

  /** Position + size the outer frame. Embedded mode fills its container instead. */
  function applyFrame(w: number, h: number, side: 'left' | 'right', pos: string) {
    if (embedded) return;
    const el = wrap;
    el.style.position = 'fixed';

    // Mobile fullscreen is applied synchronously here rather than left to the
    // rAF in schedule(). Painting even one frame of desktop geometry — a 400px
    // box pinned 20px off the corner — is a visible flash of the host page.
    if (mobileFull()) {
      syncViewport();
      return;
    }
    el.style.removeProperty('--vv-top');
    el.style.removeProperty('--kb-bottom');
    el.style.width = isOpen ? `${w}px` : `${LAUNCHER}px`;
    el.style.height = isOpen ? `${h + LAUNCHER_STACK}px` : `${LAUNCHER}px`;
    el.style.maxHeight = isOpen ? 'calc(100vh - 32px)' : '';
    el.style[side] = '20px';
    el.style[side === 'left' ? 'right' : 'left'] = 'auto';
    if (pos.includes('top')) {
      el.style.top = '20px';
      el.style.bottom = 'auto';
    } else {
      el.style.bottom = '20px';
      el.style.top = 'auto';
    }
  }

  function syncHeader() {
    const header = wrap.querySelector<HTMLElement>('.header');
    if (header) header.style.display = tab === 'chat' ? 'flex' : 'none';
  }

  function scrollDown() {
    const el = wrap.querySelector<HTMLElement>('.messages');
    if (el) el.scrollTop = el.scrollHeight;
  }

  /* ---- events ---- */
  function bind() {
    const launcherEl = wrap.querySelector<HTMLElement>('.launcher');
    if (launcherEl) {
      launcherEl.onclick = () => {
        isOpen = !isOpen;
        // Re-resolve on every open so the panel always starts where the
        // customer configured, not wherever the visitor left it last time.
        if (isOpen) tab = openingTab();
        render();
      };
    }

    wrap.querySelectorAll<HTMLElement>('[data-act]').forEach((el) => {
      el.onclick = () => {
        const act = el.dataset.act;
        if (act === 'close') {
          isOpen = false;
          render();
        } else if (act === 'to-chat') {
          setTab('chat');
        } else if (act === 'to-home') {
          setTab('home');
        } else if (act === 'clear') {
          messages = [];
          showStarters = true;
          disabled = false;
          host.onClear?.();
          render();
        }
      };
    });

    bindTabs();
    bindStarters();

    const ta = wrap.querySelector<HTMLTextAreaElement>('textarea');
    const send = wrap.querySelector<HTMLButtonElement>('.send');
    if (ta && send) {
      const submit = () => void sendMessage(ta.value);
      send.onclick = submit;
      ta.onkeydown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          submit();
        }
      };
      ta.oninput = () => {
        ta.style.height = 'auto';
        ta.style.height = `${Math.min(ta.scrollHeight, 96)}px`;
        send.disabled = !ta.value.trim();
      };
      send.disabled = !ta.value.trim();
    }
  }

  function bindTabs() {
    wrap.querySelectorAll<HTMLElement>('[data-tab]').forEach((el) => {
      el.onclick = () => setTab(el.dataset.tab as 'home' | 'chat');
    });
  }

  function bindStarters() {
    wrap.querySelectorAll<HTMLElement>('[data-starter]').forEach((el) => {
      el.onclick = () => void sendMessage(starters()[Number(el.dataset.starter)] || '');
    });
  }

  /** Tab switching only toggles classes and swaps the two tab buttons — it never
   *  rebuilds the chat DOM, so an in-flight streaming reply keeps rendering while
   *  the visitor browses Home. */
  function setTab(next: 'home' | 'chat') {
    tab = next;
    wrap.querySelectorAll<HTMLElement>('.view').forEach((v) => {
      v.classList.toggle('active', v.dataset.view === next);
    });
    const bar = wrap.querySelector<HTMLElement>('.tabbar');
    if (bar) {
      // Re-render just the tab bar so the icons swap outline ↔ solid.
      bar.innerHTML = `${tabHtml('home')}${tabHtml('chat')}`;
      bindTabs();
    }
    syncHeader();
    if (next === 'chat') {
      scrollDown();
      if (!embedded && window.innerWidth > 640) {
        wrap.querySelector<HTMLTextAreaElement>('textarea')?.focus();
      }
    }
  }

  function repaintMessages() {
    const el = wrap.querySelector<HTMLElement>('.messages');
    if (!el) return;
    el.innerHTML = messagesHtml();
    bindStarters();
    scrollDown();
  }

  /* ---- send ---- */
  async function sendMessage(raw: string) {
    const text = String(raw || '').trim();
    if (!text || disabled || sending) return;
    const now = Date.now();
    if (now - lastSend < 1200) return; // debounce double-fires
    lastSend = now;

    if (tab !== 'chat') setTab('chat');

    sending = true;
    const ta = wrap.querySelector<HTMLTextAreaElement>('textarea');
    if (ta) {
      ta.value = '';
      ta.style.height = 'auto';
    }
    const sendBtn = wrap.querySelector<HTMLButtonElement>('.send');
    if (sendBtn) sendBtn.disabled = true;

    // History excludes the message being sent and the pending bot bubble.
    const history: ChatTurn[] = messages
      .filter((m) => m.text && !m.typing)
      .map((m) => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.text }));

    messages.push({ text, sender: 'user', time: timeNow() });
    showStarters = false;
    const botIdx = messages.length;
    messages.push({ text: '', sender: 'bot', time: timeNow(), typing: true });
    repaintMessages();

    const ref: { bubble: HTMLElement | null } = { bubble: null };
    let frame = 0;
    let first = true;

    try {
      const result = await host.send(text, history, (token) => {
        const list = wrap.querySelector<HTMLElement>('.messages');
        if (!list) return;
        if (first) {
          first = false;
          messages[botIdx].typing = false;
          messages[botIdx].text = '';
          const all = list.querySelectorAll<HTMLElement>('.msg.bot .bubble');
          ref.bubble = all[all.length - 1] ?? null;
          if (ref.bubble) {
            ref.bubble.innerHTML = '';
            ref.bubble.classList.add('streaming');
          }
        }
        messages[botIdx].text += token;
        if (!frame) {
          frame = requestAnimationFrame(() => {
            frame = 0;
            if (ref.bubble) ref.bubble.innerHTML = formatMd(messages[botIdx].text);
            list.scrollTop = list.scrollHeight;
          });
        }
      });

      if (frame) cancelAnimationFrame(frame);
      if (ref.bubble) {
        ref.bubble.innerHTML = formatMd(messages[botIdx].text);
        ref.bubble.classList.remove('streaming');
      }

      if (!result.success) {
        if (result._status === 429 || result._status === 403 || result._status === 402) {
          messages.splice(botIdx, 1);
          disabled = true;
          disabledMsg =
            result.reason === 'visitor_daily_limit'
              ? 'You have reached the daily conversation limit. Please try again tomorrow.'
              : result.reason === 'no_subscription'
                // PaperBrainz addition. 402 means the owner's subscription
                // lapsed mid-session. The visitor is not the buyer, so they get
                // a neutral outage message rather than the owner's billing state.
                ? 'This chatbot is currently unavailable. Please try again later.'
                : 'This assistant has reached its conversation limit for this month.';
          sending = false;
          render();
          return;
        }
        messages[botIdx] = {
          text: result.error || 'Sorry, something went wrong. Please try again.',
          sender: 'bot',
          time: timeNow(),
        };
        repaintMessages();
      } else if (!messages[botIdx].text) {
        // Stream produced nothing — don't leave an empty bubble behind.
        messages[botIdx] = {
          text: 'Sorry, I had trouble answering that. Please try again.',
          sender: 'bot',
          time: timeNow(),
        };
        repaintMessages();
      }
    } catch {
      if (frame) cancelAnimationFrame(frame);
      messages[botIdx] = {
        text: 'I could not connect. Please check your internet connection and try again.',
        sender: 'bot',
        time: timeNow(),
      };
      repaintMessages();
    } finally {
      sending = false;
      const btn = wrap.querySelector<HTMLButtonElement>('.send');
      const box = wrap.querySelector<HTMLTextAreaElement>('textarea');
      if (btn && box) btn.disabled = !box.value.trim();
    }
  }

  /* ---- mobile viewport (storefront only) ----
   * The frame is pinned to the LAYOUT viewport — inset 0, 100% × 100% — which
   * does not shrink or shift when the keyboard opens. That is the whole point.
   * The previous version sized the frame FROM visualViewport, and iOS does not
   * reliably fire a final event: dismiss the keyboard by dragging it, or let an
   * in-app browser collapse its toolbar, and the last geometry you were told
   * about is wrong. The frame then stood a partial screen tall, in the wrong
   * place, with the merchant's page visible above and below it.
   *
   * visualViewport is still read, but only to measure two insets applied as
   * padding on the panel, keeping content inside the region the visitor can
   * actually see:
   *   --vv-top    how far iOS scrolled the visual viewport to reveal the input
   *   --kb-bottom what the keyboard (and any bottom browser chrome) covers
   * A stale inset misplaces content inside the panel. It cannot expose the page
   * behind it, because the panel is always full-screen.
   */
  let raf = 0;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  /** Whether the last render was the mobile fullscreen one. */
  let lastMobileFull = false;

  /** True only while the storefront widget owns the whole screen. */
  function mobileFull(): boolean {
    return !embedded && isOpen && window.innerWidth <= 640;
  }

  function syncViewport() {
    if (!mobileFull()) return;
    const el = wrap;
    const vv = window.visualViewport;

    el.style.top = '0';
    el.style.left = '0';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.width = '100%';
    el.style.height = '100%';
    el.style.maxHeight = 'none';

    const layoutH = document.documentElement.clientHeight || window.innerHeight;
    const top = vv ? Math.max(0, Math.round(vv.offsetTop)) : 0;
    const bottom = vv ? Math.max(0, Math.round(layoutH - vv.height - vv.offsetTop)) : 0;
    el.style.setProperty('--vv-top', `${top}px`);
    el.style.setProperty('--kb-bottom', `${bottom}px`);
    scrollDown();
  }

  function schedule() {
    if (embedded) return;
    // A rotate — or a desktop window dragged across the 640px breakpoint with the
    // panel open — flips the mode. The frame geometry, the media query and the
    // body lock all have to move together, so that needs a full render rather
    // than an inset sync. render() updates lastMobileFull before calling back in,
    // so this cannot recurse.
    if (mobileFull() !== lastMobileFull) {
      render();
      return;
    }
    // Bail before allocating a frame when there is nothing to sync — this also
    // runs on every host-page scroll event, on desktop included.
    if (raf || !lastMobileFull) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      syncViewport();
    });
  }

  /** iOS fires focus/blur before the keyboard animation finishes, and sometimes
   *  fires no visualViewport event at all when it is dismissed by dragging. Sync
   *  now for the common case and once more after the animation has settled. */
  function settle() {
    schedule();
    clearTimeout(settleTimer);
    settleTimer = setTimeout(syncViewport, 350);
  }

  /* ---- body scroll lock (storefront, mobile fullscreen) ----
   * Without this the host page keeps scrolling underneath the open panel. On
   * iOS a fixed element is only re-pinned at scroll end, so the panel visibly
   * drifts mid-gesture, and momentum scrolling rubber-bands the merchant's page
   * into view around it. `position: fixed` on <body> is the only lock iOS
   * honours during momentum — `overflow: hidden` alone is not. The visitor's
   * scroll position is captured and restored on close, so closing the chat
   * leaves them exactly where they were.
   *
   * Whatever the host page had set inline is saved and put back verbatim; this
   * must not be a one-way write into someone else's document. */
  const LOCK_PROPS = ['position', 'top', 'left', 'right', 'width', 'overflow'];
  let savedBody: Record<string, string> | null = null;
  let lockedY = 0;

  function lockScroll() {
    if (embedded || savedBody) return;
    const b = document.body.style;
    savedBody = {};
    for (const p of LOCK_PROPS) savedBody[p] = b.getPropertyValue(p);
    lockedY = window.scrollY;
    b.setProperty('position', 'fixed');
    b.setProperty('top', `-${lockedY}px`);
    b.setProperty('left', '0');
    b.setProperty('right', '0');
    b.setProperty('width', '100%');
    b.setProperty('overflow', 'hidden');
  }

  function unlockScroll() {
    if (!savedBody) return;
    const b = document.body.style;
    for (const p of LOCK_PROPS) {
      const v = savedBody[p];
      if (v) b.setProperty(p, v);
      else b.removeProperty(p);
    }
    savedBody = null;
    window.scrollTo(0, lockedY);
  }

  if (!embedded) {
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('focusin', settle, true);
    window.addEventListener('focusout', settle, true);
  }

  render();

  return {
    update(next: WidgetSettings) {
      s = { ...next };
      render();
    },
    destroy() {
      if (!embedded) {
        window.visualViewport?.removeEventListener('resize', schedule);
        window.visualViewport?.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        window.removeEventListener('orientationchange', schedule);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('focusin', settle, true);
        window.removeEventListener('focusout', settle, true);
        clearTimeout(settleTimer);
        unlockScroll();
      }
      wrap.remove();
      styleEl.remove();
    },
  };
}
